/**
 * NotificationsRenderer — transient screen-space notifications:
 * wind shift, void rise, round start overlay, turn timer, target blast intro.
 *
 * Extracted from HUDRenderer as a composition class.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { TURN_FLOW, VOID, WORLD, ROUND_START, TARGET_BLAST_INTRO, Z } from '../../constants';
import { formatWind } from '../../game/wind';
import type { GameState } from '../../types';

export class NotificationsRenderer {
  private windShiftTitle: Text;
  private windShiftValue: Text;
  private voidRiseTitle: Text;
  private voidRiseSubtitle: Text;
  private roundStartText: Text;
  private roundStartShowTime: number = 0;

  constructor(uiContainer: Container) {
    // Wind shift notification — title
    this.windShiftTitle = new Text({
      text: 'WIND SHIFT',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.WIND_SHIFT_TITLE_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0x00ffff,
        stroke: { color: 0x000000, width: 4 },
      }),
    });
    this.windShiftTitle.anchor.set(0.5, 0.5);
    this.windShiftTitle.position.set(WORLD.CANVAS_WIDTH / 2, 280);
    this.windShiftTitle.zIndex = Z.HUD_BASE;
    this.windShiftTitle.visible = false;
    uiContainer.addChild(this.windShiftTitle);

    // Wind shift notification — value
    this.windShiftValue = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.WIND_SHIFT_VALUE_FONT_SIZE,
        fill: 0xffffff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.windShiftValue.anchor.set(0.5, 0.5);
    this.windShiftValue.position.set(WORLD.CANVAS_WIDTH / 2, 310);
    this.windShiftValue.zIndex = Z.HUD_BASE;
    this.windShiftValue.visible = false;
    uiContainer.addChild(this.windShiftValue);

    // Void rise notification — title
    this.voidRiseTitle = new Text({
      text: 'VOID RISING',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.VOID_RISE_TITLE_FONT_SIZE,
        fontWeight: 'bold',
        fill: VOID.EDGE_COLOR,
        stroke: { color: 0x000000, width: 4 },
      }),
    });
    this.voidRiseTitle.anchor.set(0.5, 0.5);
    this.voidRiseTitle.position.set(WORLD.CANVAS_WIDTH / 2, 230);
    this.voidRiseTitle.zIndex = Z.HUD_BASE;
    this.voidRiseTitle.visible = false;
    uiContainer.addChild(this.voidRiseTitle);

    // Void rise notification — subtitle
    this.voidRiseSubtitle = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: TURN_FLOW.VOID_RISE_SUBTITLE_FONT_SIZE,
        fill: 0xff00ff,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.voidRiseSubtitle.anchor.set(0.5, 0.5);
    this.voidRiseSubtitle.position.set(WORLD.CANVAS_WIDTH / 2, 260);
    this.voidRiseSubtitle.zIndex = Z.HUD_BASE;
    this.voidRiseSubtitle.visible = false;
    uiContainer.addChild(this.voidRiseSubtitle);

    // Round start overlay
    this.roundStartText = new Text({
      text: 'ROUND 1',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: ROUND_START.TEXT_FONT_SIZE,
        fontWeight: 'bold',
        fill: 0xffffff,
        align: 'center',
        stroke: { color: 0x000000, width: 5 },
      }),
    });
    this.roundStartText.anchor.set(0.5, 0.5);
    this.roundStartText.position.set(WORLD.CANVAS_WIDTH / 2, WORLD.CANVAS_HEIGHT / 2);
    this.roundStartText.zIndex = Z.HUD_DIALOG;
    this.roundStartText.visible = false;
    uiContainer.addChild(this.roundStartText);

  }

  hide(): void {
    this.windShiftTitle.visible = false;
    this.windShiftValue.visible = false;
    this.voidRiseTitle.visible = false;
    this.voidRiseSubtitle.visible = false;
    this.roundStartText.visible = false;
  }

  updateRoundStart(progress: number, roundNumber: number, visible: boolean): void {
    this.roundStartText.visible = visible;
    if (!visible) return;

    this.roundStartText.text = `ROUND ${roundNumber}`;

    if (progress <= 0.01) {
      this.roundStartShowTime = Date.now();
    }

    const elapsed = (Date.now() - this.roundStartShowTime) / 1000;
    const enterT = Math.min(1, elapsed / ROUND_START.TEXT_ENTER_DURATION);
    const eased = 1 - (1 - enterT) * (1 - enterT);
    const scale = ROUND_START.TEXT_ENTER_SCALE + (1 - ROUND_START.TEXT_ENTER_SCALE) * eased;
    this.roundStartText.scale.set(scale);

    const timeRemaining = ROUND_START.DURATION * (1 - progress);
    if (timeRemaining <= ROUND_START.FADE_OUT_AT) {
      this.roundStartText.alpha = timeRemaining / ROUND_START.FADE_OUT_AT;
    } else {
      this.roundStartText.alpha = Math.min(1, enterT * 2);
    }
  }

  updateTargetBlastIntro(step: number, timer: number): void {
    const steps = TARGET_BLAST_INTRO.STEPS;
    if (step < 0 || step >= steps.length) {
      this.roundStartText.visible = false;
      return;
    }

    const stepDef = steps[step];
    this.roundStartText.visible = true;
    this.roundStartText.text = stepDef.text;
    this.roundStartText.style.fill = stepDef.color;
    this.roundStartText.style.fontSize = stepDef.fontSize;

    const elapsed = stepDef.duration - timer;
    const enterT = Math.min(1, elapsed / 0.15);
    const eased = 1 - (1 - enterT) * (1 - enterT);
    const scale = 1.3 + (1 - 1.3) * eased;
    this.roundStartText.scale.set(scale);
    this.roundStartText.alpha = Math.min(1, enterT * 3);
  }

  update(state: GameState, dt: number): void {
    // Round start text (hidden when not in round_start phase)
    if (state.phase !== 'round_start') {
      this.roundStartText.visible = false;
    }

    // Wind shift notification
    if (state.windShiftPending) {
      this.windShiftTitle.visible = true;
      this.windShiftValue.visible = true;
      const shiftInfo = formatWind(state.wind);
      this.windShiftValue.text = shiftInfo.display;
      const alpha = Math.min(1, state.windShiftTimer);
      this.windShiftTitle.alpha = alpha;
      this.windShiftValue.alpha = alpha;
    } else {
      this.windShiftTitle.visible = false;
      this.windShiftValue.visible = false;
    }

    // Void rise notification
    if (state.voidRisePending) {
      this.voidRiseTitle.visible = true;
      this.voidRiseSubtitle.visible = true;
      this.voidRiseSubtitle.text = '';
      const alpha = Math.min(1, state.voidRiseTimer);
      this.voidRiseTitle.alpha = alpha;
      this.voidRiseSubtitle.alpha = alpha;
    } else {
      this.voidRiseTitle.visible = false;
      this.voidRiseSubtitle.visible = false;
    }
  }

  destroy(): void {
    this.windShiftTitle.destroy();
    this.windShiftValue.destroy();
    this.voidRiseTitle.destroy();
    this.voidRiseSubtitle.destroy();
    this.roundStartText.destroy();
  }
}
